import { useEffect, useState } from "react";
import axios from "axios";
import { ShieldStar, Eye, EyeSlash, Receipt, Calculator, Wallet as WalletIcon, Plugs } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `\u20b9${(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const Toggle = ({ checked, onChange, testid }) => (
  <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} className="data-[state=checked]:bg-emerald-600" />
);

const WalletSection = () => {
  const [rows, setRows] = useState([]);
  const [adj, setAdj] = useState(null); // {company_id, name, action}
  const [amt, setAmt] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [upi, setUpi] = useState({ upi_id: "", payee_name: "" });
  const [upiSaved, setUpiSaved] = useState("");
  const [requests, setRequests] = useState([]);
  const [prem, setPrem] = useState({ requests: [], prices: { yearly_price: 17000, monthly_price: 1700 } });
  const [premPrices, setPremPrices] = useState({ yearly_price: "", monthly_price: "" });
  const [premSaved, setPremSaved] = useState("");

  const load = async () => {
    try {
      const [r, s, q] = await Promise.all([
        axios.get(`${API}/super-admin/wallets`),
        axios.get(`${API}/super-admin/wallet-settings`),
        axios.get(`${API}/super-admin/wallet-requests`),
      ]);
      setRows(r.data); setUpi(s.data); setRequests(q.data);
      try {
        const p = await axios.get(`${API}/super-admin/premium/requests`);
        setPrem(p.data);
        setPremPrices({ yearly_price: String(p.data.prices.yearly_price), monthly_price: String(p.data.prices.monthly_price) });
      } catch { /* noop */ }
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const savePremPrices = async () => {
    try {
      await axios.put(`${API}/super-admin/premium/prices`, {
        yearly_price: parseFloat(premPrices.yearly_price), monthly_price: parseFloat(premPrices.monthly_price),
      });
      setPremSaved("Saved ✓"); setTimeout(() => setPremSaved(""), 2500); load();
    } catch (e) { setPremSaved(e.response?.data?.detail || "Save failed"); }
  };

  const decidePremium = async (txnId, action) => {
    try {
      const r = await axios.post(`${API}/super-admin/premium/${txnId}/${action}`);
      if (action === "approve" && r.data.active_from) {
        alert(`✓ Plan approved — ${r.data.active_from} se ${r.data.active_till} tak active`);
      }
      load();
    } catch (e) { alert(e.response?.data?.detail || "Action failed"); }
  };

  const setPremium = async (companyId, action, plan) => {
    if (action === "off" && !window.confirm("Is company ka Premium plan turant OFF kar dein?")) return;
    try {
      const r = await axios.post(`${API}/super-admin/premium/set`, { company_id: companyId, action, plan });
      if (r.data.status === "on") alert(`✓ ${plan.toUpperCase()} plan ON — ${r.data.active_from} se ${r.data.active_till} tak`);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Action failed"); }
  };

  const saveUpi = async () => {
    try {
      await axios.put(`${API}/super-admin/wallet-settings`, upi);
      setUpiSaved("Saved ✓"); setTimeout(() => setUpiSaved(""), 2500);
    } catch (e) { setUpiSaved(e.response?.data?.detail || "Save failed"); }
  };

  const decide = async (txnId, action) => {
    try {
      await axios.post(`${API}/super-admin/wallet-requests/${txnId}/${action}`);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Action failed"); }
  };

  const submit = async () => {
    const amount = parseFloat(amt);
    if (!amount || amount <= 0) { setErr("Valid amount daalein"); return; }
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/super-admin/wallets/adjust`, { company_id: adj.company_id, amount, action: adj.action, note });
      setAdj(null); setAmt(""); setNote(""); load();
    } catch (e) { setErr(e.response?.data?.detail || "Adjust failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border-2 border-purple-600 rounded-sm mb-10" data-testid="sa-wallet-section">
      <div className="flex items-center gap-2 px-5 py-4 border-b-2 border-purple-600 bg-purple-50">
        <WalletIcon size={22} weight="duotone" className="text-purple-700" />
        <h2 className="text-lg font-black text-slate-900 uppercase tracking-wide">Wallet Management</h2>
        <span className="ml-auto text-xs font-bold bg-purple-100 text-purple-800 px-2 py-1 rounded-sm">PAYMENTS</span>
      </div>

      {/* UPI settings — money receiving account for the free manual QR flow */}
      <div className="px-5 py-4 border-b border-slate-200 bg-slate-50 flex items-end gap-3 flex-wrap" data-testid="sa-upi-settings">
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Payment Receiving UPI ID</label>
          <input value={upi.upi_id} onChange={(e) => setUpi({ ...upi, upi_id: e.target.value })} data-testid="sa-upi-id-input"
            placeholder="yourname@upi" className="px-3 py-2 border border-slate-300 rounded-sm text-sm w-56 font-mono" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Payee Name</label>
          <input value={upi.payee_name} onChange={(e) => setUpi({ ...upi, payee_name: e.target.value })} data-testid="sa-upi-payee-input"
            placeholder="LokTech" className="px-3 py-2 border border-slate-300 rounded-sm text-sm w-44" />
        </div>
        <button onClick={saveUpi} data-testid="sa-upi-save-btn" className="px-4 py-2 bg-purple-700 text-white rounded-sm text-sm font-bold hover:bg-purple-800">Save UPI</button>
        {upiSaved && <span className="text-xs font-semibold text-emerald-700 pb-2" data-testid="sa-upi-saved-msg">{upiSaved}</span>}
        <p className="w-full text-[11px] text-slate-500">Users will pay by scanning this UPI ID's QR. Once they submit the UTR, the request will appear below — approve it to credit their wallet.</p>
      </div>

      {/* Pending recharge requests */}
      {requests.length > 0 && (
        <div className="border-b border-slate-200" data-testid="sa-wallet-requests">
          <div className="px-5 py-2 bg-amber-50 border-b border-amber-200">
            <p className="text-xs font-black uppercase tracking-widest text-amber-800">Pending Recharge Requests ({requests.length})</p>
          </div>
          <div className="overflow-x-auto sticky-table">
            <table className="w-full text-sm">
              <thead className="sticky-thead bg-slate-100 text-slate-600 uppercase text-[11px] font-bold">
                <tr>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">User</th>
                  <th className="px-4 py-2 text-left">UTR</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2 text-center">Decision</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((q) => (
                  <tr key={q.id} className="border-b border-slate-100" data-testid={`sa-wallet-request-${q.id}`}>
                    <td className="px-4 py-2 font-mono text-xs">{(q.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-2 text-xs">{q.user_email}</td>
                    <td className="px-4 py-2 font-mono text-xs font-bold">{q.utr}</td>
                    <td className="px-4 py-2 text-right font-mono font-black text-emerald-700">{fmt(q.amount)}</td>
                    <td className="px-4 py-2 text-center">
                      <button onClick={() => decide(q.id, "approve")} data-testid={`sa-wallet-approve-${q.id}`} className="text-xs font-bold text-white bg-emerald-600 rounded-sm px-3 py-1 mr-2 hover:bg-emerald-700">Approve</button>
                      <button onClick={() => decide(q.id, "reject")} data-testid={`sa-wallet-reject-${q.id}`} className="text-xs font-bold text-red-700 border border-red-300 rounded-sm px-3 py-1 hover:bg-red-50">Reject</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* ---- PREMIUM PLANS: prices + purchase requests ---- */}
      <div className="border-t-2 border-amber-400 bg-amber-50/50 px-5 py-4" data-testid="sa-premium-section">
        <h3 className="text-sm font-black text-slate-900 mb-1">👑 Premium Plans (free usage for the full year/month)</h3>
        <p className="text-[11px] text-slate-500 mb-3">On approval, ALL per-use charges for that company stop immediately (yearly = 365 days, monthly = 30 days).</p>
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Yearly Price (₹)</label>
            <input type="number" value={premPrices.yearly_price} onChange={(e) => setPremPrices({ ...premPrices, yearly_price: e.target.value })}
              data-testid="sa-premium-yearly-price" className="px-3 py-2 border border-slate-300 rounded-sm text-sm w-36" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Monthly Price (₹)</label>
            <input type="number" value={premPrices.monthly_price} onChange={(e) => setPremPrices({ ...premPrices, monthly_price: e.target.value })}
              data-testid="sa-premium-monthly-price" className="px-3 py-2 border border-slate-300 rounded-sm text-sm w-36" />
          </div>
          <button onClick={savePremPrices} data-testid="sa-premium-save-prices" className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-sm text-sm font-black">Save Prices</button>
          {premSaved && <span className="text-xs font-bold text-emerald-700" data-testid="sa-premium-saved-msg">{premSaved}</span>}
        </div>
        {(prem.requests || []).length === 0 ? (
          <p className="text-xs text-slate-400 py-2" data-testid="sa-premium-no-requests">No premium requests yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm bg-white border border-slate-200 rounded-sm">
              <thead className="bg-slate-100 text-slate-600 uppercase text-[10px] font-bold">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Company / User</th>
                  <th className="px-3 py-2 text-left">Plan</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">UTR</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Active From → Till</th>
                  <th className="px-3 py-2 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {prem.requests.map((q) => (
                  <tr key={q.id} className="border-b border-slate-100" data-testid={`sa-premium-req-${q.id}`}>
                    <td className="px-3 py-2 font-mono text-[11px]">{(q.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2 text-xs"><b>{q.company_name || "—"}</b><br /><span className="text-slate-500">{q.user_email}</span></td>
                    <td className="px-3 py-2"><span className={`text-[10px] font-black px-2 py-0.5 rounded-sm ${q.plan === "yearly" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>{(q.plan || "").toUpperCase()}</span></td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{fmt(q.amount)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{q.utr || "—"}</td>
                    <td className="px-3 py-2 text-[10px] font-bold uppercase">{q.status}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">{q.active_from ? `${q.active_from} → ${q.active_till}` : (q.status === "pending" ? "set on approval" : "—")}</td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      {q.status === "pending" ? (
                        <>
                          <button onClick={() => decidePremium(q.id, "approve")} data-testid={`sa-premium-approve-${q.id}`} className="text-xs font-bold text-white bg-emerald-600 rounded-sm px-3 py-1 mr-2 hover:bg-emerald-700">Approve / ON</button>
                          <button onClick={() => decidePremium(q.id, "reject")} data-testid={`sa-premium-reject-${q.id}`} className="text-xs font-bold text-red-700 border border-red-300 rounded-sm px-3 py-1 hover:bg-red-50">Reject</button>
                        </>
                      ) : <span className="text-slate-400 text-xs">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="overflow-x-auto sticky-table">
        <table className="w-full text-sm">
          <thead className="sticky-thead bg-slate-100 text-slate-600 uppercase text-[11px] font-bold">
            <tr>
              <th className="px-4 py-3 text-left">User</th>
              <th className="px-4 py-3 text-left">Email (User ID)</th>
              <th className="px-4 py-3 text-left">Company</th>
              <th className="px-4 py-3 text-right">Wallet Balance</th>
              <th className="px-4 py-3 text-left">Premium Plan</th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`sa-wallet-row-${r.email}`}>
                <td className="px-4 py-3 font-semibold text-slate-900">{r.name || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.email}</td>
                <td className="px-4 py-3 text-xs">{r.company_name || "—"}</td>
                <td className="px-4 py-3 text-right font-mono font-black text-emerald-700" data-testid={`sa-wallet-balance-${r.email}`}>{fmt(r.balance)}</td>
                <td className="px-4 py-3 text-xs whitespace-nowrap" data-testid={`sa-premium-cell-${r.email}`}>
                  {r.role === "super_admin" ? (
                    <span className="text-slate-400">—</span>
                  ) : r.premium_active ? (
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-amber-700">👑 {(r.premium_plan || "").toUpperCase()}<br />
                        <span className="font-mono text-[10px] text-slate-600">{r.premium_start || "?"} → {r.premium_until}</span>
                      </span>
                      <button onClick={() => setPremium(r.company_id, "off")} data-testid={`sa-premium-off-${r.email}`}
                        className="text-[10px] font-bold text-red-700 border border-red-300 rounded-sm px-2 py-1 hover:bg-red-50">OFF</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button onClick={() => setPremium(r.company_id, "on", "yearly")} data-testid={`sa-premium-on-yearly-${r.email}`}
                        className="text-[10px] font-bold text-amber-800 border border-amber-400 rounded-sm px-2 py-1 hover:bg-amber-50">ON Yearly</button>
                      <button onClick={() => setPremium(r.company_id, "on", "monthly")} data-testid={`sa-premium-on-monthly-${r.email}`}
                        className="text-[10px] font-bold text-blue-800 border border-blue-300 rounded-sm px-2 py-1 hover:bg-blue-50">ON Monthly</button>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => setAdj({ ...r, action: "add" })} data-testid={`sa-wallet-add-${r.email}`} className="text-xs font-bold text-emerald-700 border border-emerald-300 rounded-sm px-2 py-1 mr-2 hover:bg-emerald-50">+ Add</button>
                  <button onClick={() => setAdj({ ...r, action: "deduct" })} data-testid={`sa-wallet-deduct-${r.email}`} className="text-xs font-bold text-red-700 border border-red-300 rounded-sm px-2 py-1 hover:bg-red-50">− Deduct</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {adj && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="sa-wallet-adjust-modal">
          <div className="bg-white rounded-sm p-6 max-w-sm w-full">
            <h3 className="text-lg font-black text-slate-900 mb-1">{adj.action === "add" ? "Add Money" : "Deduct Money"}</h3>
            <p className="text-xs text-slate-500 mb-4">{adj.name} ({adj.email}) — current: {fmt(adj.balance)}</p>
            {err && <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-sm">{err}</div>}
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Amount (₹)</label>
            <input type="number" min="1" value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="sa-wallet-adjust-amount" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mb-3" />
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Note</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="sa-wallet-adjust-note" placeholder="Refund / bonus / subscription…" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mb-4" />
            <div className="flex gap-2">
              <button onClick={submit} disabled={busy} data-testid="sa-wallet-adjust-submit" className={`flex-1 px-4 py-2 text-white rounded-sm text-sm font-bold disabled:opacity-50 ${adj.action === "add" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}>{busy ? "…" : (adj.action === "add" ? "Add" : "Deduct")}</button>
              <button onClick={() => { setAdj(null); setErr(""); }} data-testid="sa-wallet-adjust-cancel" className="px-4 py-2 border border-slate-300 rounded-sm text-sm font-semibold">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const GspMasterSection = () => {
  const [cfg, setCfg] = useState({
    enabled: false, provider: "generic", base_url: "",
    ewb_path: "/ewb/generate", einvoice_path: "/einvoice/generate",
    client_id_set: false, client_secret_set: false,
  });
  const [form, setForm] = useState({ client_id: "", client_secret: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/super-admin/gsp-config`);
      setCfg(r.data);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      const payload = {
        enabled: cfg.enabled,
        provider: cfg.provider,
        base_url: cfg.base_url,
        ewb_path: cfg.ewb_path,
        einvoice_path: cfg.einvoice_path,
      };
      if (form.client_id) payload.client_id = form.client_id;
      if (form.client_secret) payload.client_secret = form.client_secret;
      await axios.put(`${API}/super-admin/gsp-config`, payload);
      setForm({ client_id: "", client_secret: "" });
      setMsg("Saved ✓"); setTimeout(() => setMsg(""), 2500);
      load();
    } catch (e) { setMsg(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border-2 border-blue-600 rounded-sm mb-10" data-testid="sa-gsp-section">
      <div className="flex items-center gap-2 px-5 py-4 border-b-2 border-blue-600 bg-blue-50">
        <Plugs size={22} weight="duotone" className="text-blue-700" />
        <h2 className="text-lg font-black text-slate-900 uppercase tracking-wide">Master GSP — E-Way Bill + E-Invoice</h2>
        <span className="ml-auto text-xs font-bold bg-blue-100 text-blue-800 px-2 py-1 rounded-sm">PLATFORM PLAN</span>
      </div>
      <div className="p-5 space-y-4">
        <p className="text-xs text-slate-600 leading-relaxed">
          Yahan aap platform ka <b>ek hi GSP subscription</b> plug karo — saare tenants (Admins) isi ke through EWB &amp; e-Invoice generate karenge.
          ₹1 is deducted from their wallet per generation.  Recommended: <b>Masters India / IRIS / Cygnet</b> (lowest per-doc pricing).
        </p>
        {msg && <div className="text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-sm px-3 py-2" data-testid="sa-gsp-msg">{msg}</div>}

        <div className="flex items-center gap-3">
          <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="sa-gsp-enabled" className="data-[state=checked]:bg-blue-600" />
          <span className="text-sm font-semibold">{cfg.enabled ? "Enabled" : "Disabled"} — GSP integration</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Provider</label>
            <select value={cfg.provider} onChange={(e) => setCfg({ ...cfg, provider: e.target.value })} data-testid="sa-gsp-provider" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">
              <option value="generic">Generic REST (any GSP)</option>
              <option value="masters_india">Masters India (GSTHero)</option>
              <option value="cygnet">Cygnet</option>
              <option value="iris">IRIS Business (IRISGST)</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Base URL</label>
            <input value={cfg.base_url} onChange={(e) => setCfg({ ...cfg, base_url: e.target.value })} data-testid="sa-gsp-base-url"
              placeholder="https://gstapi.mastersindia.co/api/v1"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">EWB Path</label>
            <input value={cfg.ewb_path} onChange={(e) => setCfg({ ...cfg, ewb_path: e.target.value })} data-testid="sa-gsp-ewb-path"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">E-Invoice Path</label>
            <input value={cfg.einvoice_path} onChange={(e) => setCfg({ ...cfg, einvoice_path: e.target.value })} data-testid="sa-gsp-einv-path"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">
              Client ID {cfg.client_id_set && <span className="text-emerald-600 ml-1">(saved — leave blank to keep)</span>}
            </label>
            <input type="password" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="sa-gsp-client-id"
              placeholder={cfg.client_id_set ? "••••••••" : "GSP client_id"}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">
              Client Secret {cfg.client_secret_set && <span className="text-emerald-600 ml-1">(saved — leave blank to keep)</span>}
            </label>
            <input type="password" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} data-testid="sa-gsp-client-secret"
              placeholder={cfg.client_secret_set ? "••••••••" : "GSP client_secret"}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono" />
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button onClick={save} disabled={busy} data-testid="sa-gsp-save"
            className="px-5 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-sm text-sm font-bold disabled:opacity-50">
            {busy ? "Saving…" : "Save Master GSP Config"}
          </button>
          <span className="text-xs text-slate-500">Per generation wallet charge: <b>EWB ₹1.00</b> · <b>E-Invoice ₹1.00</b></span>
        </div>
      </div>
    </div>
  );
};

export default function SuperAdmin() {
  const [users, setUsers] = useState([]);
  const [revealed, setRevealed] = useState({});
  const [msg, setMsg] = useState("");

  const fetchUsers = async () => {
    try {
      const r = await axios.get(`${API}/super-admin/users`);
      setUsers(r.data);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Failed to load users");
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const toggle = async (uid, field, value) => {
    try {
      await axios.put(`${API}/super-admin/users/${uid}/features`, { [field]: value });
      setUsers((us) => us.map((u) => (u.id === uid ? { ...u, [field]: value } : u)));
    } catch (e) {
      setMsg(e.response?.data?.detail || "Update failed");
      setTimeout(() => setMsg(""), 4000);
    }
  };

  return (
    <div className="p-4 sm:p-8" data-testid="super-admin-page">
      <div className="bg-slate-900 text-white rounded-sm p-6 sm:p-8 mb-8">
        <div className="flex items-center gap-3">
          <ShieldStar size={34} weight="duotone" className="text-amber-400" />
          <div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tighter" style={{ fontFamily: "Chivo, sans-serif" }}>Super Admin Control Center</h1>
            <p className="text-slate-300 text-sm">Saare registered users, unke credentials aur feature controls — ek jagah</p>
          </div>
        </div>
      </div>

      {msg && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm mb-4" data-testid="sa-error">{msg}</div>}

      {/* ====== TOP: GST SOFTWARE ====== */}
      <div className="bg-white border-2 border-slate-900 rounded-sm mb-10" data-testid="sa-gst-section">
        <div className="flex items-center gap-2 px-5 py-4 border-b-2 border-slate-900 bg-slate-50">
          <Receipt size={22} weight="duotone" className="text-slate-900" />
          <h2 className="text-lg font-black text-slate-900 uppercase tracking-wide">GST Software — Registered Users</h2>
          <span className="ml-auto text-xs font-bold bg-emerald-100 text-emerald-800 px-2 py-1 rounded-sm">LIVE</span>
        </div>
        <div className="overflow-x-auto sticky-table">
          <table className="w-full text-sm">
            <thead className="sticky-thead bg-slate-100 text-slate-600 uppercase text-[11px] font-bold">
              <tr>
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">Email (User ID)</th>
                <th className="px-4 py-3 text-left">Password</th>
                <th className="px-4 py-3 text-left">Company</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-center">GST</th>
                <th className="px-4 py-3 text-center">Premium</th>
                <th className="px-4 py-3 text-center">Transport</th>
                <th className="px-4 py-3 text-center">Active</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className={`border-b border-slate-100 ${!u.is_active ? "opacity-50 bg-red-50" : ""}`} data-testid={`sa-user-row-${u.email}`}>
                  <td className="px-4 py-3 font-semibold text-slate-900">{u.name || "—"}<div className="text-[10px] text-slate-400">{u.mobile}</div></td>
                  <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3">
                    {u.password ? (
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs" data-testid={`sa-password-${u.email}`}>{revealed[u.id] ? u.password : "••••••••"}</span>
                        <button onClick={() => setRevealed((r) => ({ ...r, [u.id]: !r[u.id] }))} data-testid={`sa-reveal-${u.email}`} className="text-slate-400 hover:text-slate-900">
                          {revealed[u.id] ? <EyeSlash size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    ) : (
                      <span className="text-[10px] text-slate-400 italic">N/A (old account)</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">{u.company_name || "—"}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-bold px-2 py-1 rounded-sm uppercase ${u.role === "super_admin" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>{u.role}</span></td>
                  <td className="px-4 py-3 text-center"><Toggle checked={u.gst_enabled} onChange={(v) => toggle(u.id, "gst_enabled", v)} testid={`sa-gst-${u.email}`} /></td>
                  <td className="px-4 py-3 text-center"><Toggle checked={u.premium_enabled} onChange={(v) => toggle(u.id, "premium_enabled", v)} testid={`sa-premium-${u.email}`} /></td>
                  <td className="px-4 py-3 text-center"><Toggle checked={u.transport_invoice_enabled} onChange={(v) => toggle(u.id, "transport_invoice_enabled", v)} testid={`sa-transport-${u.email}`} /></td>
                  <td className="px-4 py-3 text-center"><Toggle checked={u.is_active} onChange={(v) => toggle(u.id, "is_active", v)} testid={`sa-active-${u.email}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ====== WALLET MANAGEMENT ====== */}
      <WalletSection />

      {/* ====== MASTER GSP (E-Way Bill + E-Invoice) ====== */}
      <GspMasterSection />

      {/* ====== BOTTOM: INCOME TAX MODULE ====== */}
      <div className="bg-white border-2 border-amber-500 rounded-sm" data-testid="sa-incometax-section">
        <div className="flex items-center gap-2 px-5 py-4 border-b-2 border-amber-500 bg-amber-50">
          <Calculator size={22} weight="duotone" className="text-amber-700" />
          <h2 className="text-lg font-black text-slate-900 uppercase tracking-wide">Income Tax Module — Access Control</h2>
          <span className="ml-auto text-xs font-bold bg-amber-100 text-amber-800 px-2 py-1 rounded-sm">COMING SOON</span>
        </div>
        <div className="p-5">
          <p className="text-xs text-slate-500 mb-4">Toggle ON the users who should have Income Tax module access — they can open it by selecting "Income Tax" on the login screen.</p>
          <div className="overflow-x-auto sticky-table">
            <table className="w-full text-sm">
              <thead className="sticky-thead bg-slate-100 text-slate-600 uppercase text-[11px] font-bold">
                <tr>
                  <th className="px-4 py-3 text-left">User</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-center">Income Tax Access</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100">
                    <td className="px-4 py-3 font-semibold">{u.name || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-3 text-center"><Toggle checked={u.income_tax_enabled} onChange={(v) => toggle(u.id, "income_tax_enabled", v)} testid={`sa-incometax-${u.email}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
